/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import apiClient from './client';
import {
  APIResponse,
  Car,
  CarType,
  CarInsurance,
  CarDetailsRequest,
  CarStatusOption,
  RequestRole
} from '@/types';

// Raw API response format (different from our Car type)
interface CarApiResponse {
  id: string;
  ownerId: string;
  title: string;
  description?: string;
  published?: boolean;
  showInMyEzWheels?: boolean;
  pricePerWeek?: number;
  minimumReservationDays?: number;
  carBrand?: string;
  carTypeId?: string;
  carType?: string;
  carTypeColor?: string;
  carCategoryId?: string;
  carCategory?: string;
  carCategoryJson?: string;
  carStatusId?: string;
  carStatusKey?: string;
  carStatus?: string;
  carModel?: string;
  year?: number;
  color?: string;
  interiorColor?: string;
  liabilityInsurance?: string;
  fullCoverage?: string;
  latitude?: number;
  longitude?: number;
  phoneNumber?: string;
  carPlateNumber?: string;
  carPlateNumberState?: string;
  termsConditions?: boolean;
  createdBy?: string;
  updated?: string;
  city?: string;
  state?: string;
  addressLine1?: string;
  addressLine2?: string;
  zipCode?: string;
  country?: string;
  imageUrl?: string;
  images?: string[];
  location?: string;
  priceFullTimePerWeek?: number;
  priceDayPerShiftWeek?: number;
  priceNightPerShiftWeek?: number;
  priceSecurityDeposit?: number;
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
  allowNegotiatingPrice?: boolean;
  tlc?: boolean;
  rentalType?: number;
  rating?: number;
  frequency?: number;
  isMonthlyCar?: boolean;
  isPedicabCar?: boolean;
  seaterType?: string;
  engineType?: string;
  vin?: string;
  killSwitchId?: string;
  verraTollTagAttached?: boolean;
  isExternal?: boolean;
  externalSource?: string;
  externalVehicleId?: string;
  rentersTag?: boolean;
  requestShipTag?: boolean;
  mileage?: number;
  bookingLocationId?: string | null;
  bookingPluginSourced?: boolean;
  /** Active CarTransponders rows — the toll agencies the car is linked to. */
  tollAgencies?: number;
  /** How many of those links still have no transponder number. */
  tollAgenciesWithoutTransponder?: number;
  pricingPeriod?: number;
  deleted?: boolean;
  // Booking info from API
  renterFirstName?: string;
  renterLastName?: string;
  renterPhoneNumber?: string;
  renterEmail?: string;
  bookingId?: string;
  bookingStatus?: number;
  bookingDateFrom?: string;
  bookingDateTo?: string;
  paymentNote?: string;
  paymentStatus?: boolean;
}

// Map API response to our Car type
function mapApiResponseToCar(apiCar: CarApiResponse): Car {
  return {
    id: apiCar.id,
    title: apiCar.title,
    description: apiCar.description,
    ownerId: apiCar.ownerId,
    type: {
      id: apiCar.carTypeId || '',
      name: apiCar.carType || '',
    },
    categoryId: apiCar.carCategoryId || undefined,
    categoryName: apiCar.carCategory || undefined,
    categoryNameJson: apiCar.carCategoryJson || undefined,
    statusId: apiCar.carStatusId || undefined,
    statusKey: apiCar.carStatusKey || undefined,
    statusName: apiCar.carStatus || undefined,
    imageURL: apiCar.imageUrl,
    brand: apiCar.carBrand,
    model: apiCar.carModel,
    year: apiCar.year,
    color: apiCar.color,
    interiorColor: apiCar.interiorColor,
    plateNumber: apiCar.carPlateNumber,
    plateNumberState: apiCar.carPlateNumberState 
      ? { id: '', abbreviation: apiCar.carPlateNumberState }
      : undefined,
    tlc: apiCar.tlc ? 'True' : 'False',
    vin: apiCar.vin,
    mileage: apiCar.mileage,
    bookingLocationId: apiCar.bookingLocationId,
    address1: apiCar.addressLine1,
    address2: apiCar.addressLine2,
    city: apiCar.city
      ? { name: apiCar.city, stateId: apiCar.state || '' }
      : undefined,
    zip: apiCar.zipCode,
    phone: apiCar.phoneNumber,
    latitude: apiCar.latitude,
    longitude: apiCar.longitude,
    priceFullTimePerWeek: apiCar.priceFullTimePerWeek,
    priceDayPerShiftWeek: apiCar.priceDayPerShiftWeek,
    priceNightPerShiftWeek: apiCar.priceNightPerShiftWeek,
    priceSecurityDeposit: apiCar.priceSecurityDeposit,
    priceDaily: apiCar.priceDaily,
    priceWeekly: apiCar.priceWeekly,
    priceMonthly: apiCar.priceMonthly,
    minReservationDays: apiCar.minimumReservationDays,
    allowNegotiatingPrice: apiCar.allowNegotiatingPrice,
    isPublished: apiCar.published,
    showInMyEzWheels: apiCar.showInMyEzWheels,
    termsAndConditions: apiCar.termsConditions,
    isPedicabCar: apiCar.isPedicabCar,
    isMonthlyCar: apiCar.isMonthlyCar,
    tollTagAttached: apiCar.verraTollTagAttached,
    rentersTag: apiCar.rentersTag,
    requestShipTag: apiCar.requestShipTag,
    isExternal: apiCar.isExternal,
    externalSource: apiCar.externalSource,
    externalVehicleId: apiCar.externalVehicleId,
    insurance: apiCar.liabilityInsurance
      ? { id: '', name: apiCar.liabilityInsurance }
      : undefined,
    fullCoverageInsurance: apiCar.fullCoverage
      ? { id: '', name: apiCar.fullCoverage }
      : undefined,
    rentalType: apiCar.rentalType,
    pricingPeriod: apiCar.pricingPeriod,
    // Booking info
    renterFirstName: apiCar.renterFirstName,
    renterLastName: apiCar.renterLastName,
    renterPhoneNumber: apiCar.renterPhoneNumber,
    renterEmail: apiCar.renterEmail,
    bookingId: apiCar.bookingId,
    bookingStatus: apiCar.bookingStatus,
    bookingDateFrom: apiCar.bookingDateFrom,
    bookingDateTo: apiCar.bookingDateTo,
    updated: apiCar.updated,
    paymentNote: apiCar.paymentNote,
    paymentStatus: apiCar.paymentStatus,
    // Toll coverage — comes with the row, which is what lets the list colour and filter without a
    // second call.
    tollAgencies: apiCar.tollAgencies ?? 0,
    tollAgenciesWithoutTransponder: apiCar.tollAgenciesWithoutTransponder ?? 0,
  };
}

interface CarItemResponse {
  id: string;
  title: string;
  description?: string;
  ownerId: string;
  type: CarType;
  imageURL?: string;
  brand?: string;
  model?: string;
  year?: number;
  color?: string;
  interiorColor?: string;
  plateNumber?: string;
  plateNumberState?: { id: string; abbreviation: string };
  tlc?: string;
  vin?: string;
  address1?: string;
  address2?: string;
  city?: { id?: string; name: string; stateId: string };
  zip?: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  priceFullTimePerWeek?: number;
  priceDayPerShiftWeek?: number;
  priceNightPerShiftWeek?: number;
  priceSecurityDeposit?: number;
  minReservationDays?: number;
  allowNegotiatingPrice?: boolean;
  isPublished?: boolean;
  termsAndConditions?: boolean;
  isPedicabCar?: boolean;
  isMonthlyCar?: boolean;
  tollTagAttached?: boolean;
  rentersTag?: boolean;
  requestShipTag?: boolean;
  isExternal?: boolean;
  insurance?: CarInsurance;
  fullCoverageInsurance?: CarInsurance;
  rentalType?: number;
}

export interface ImportVehicleRow {
  VIN?: string;
  CarPlateNumber?: string;
  CarPlateNumberState?: string;
  Title?: string;
  Description?: string;
  CarBrand?: string;
  CarModel?: string;
  Year?: number;
  Color?: string;
  InteriorColor?: string;
  City?: string;
  StateId?: string;
  AddressLine1?: string;
  AddressLine2?: string;
  ZipCode?: string;
  PhoneNumber?: string;
  PriceFullTimePerWeek?: number;
  PriceDayPerShiftWeek?: number;
  PriceNightPerShiftWeek?: number;
  PriceSecurityDeposit?: number;
  MinimumReservationDays?: number;
  LiabilityInsuranceId?: string;
  FullCoverageId?: string;
  LiabilityInsuranceName?: string;
  FullCoverageName?: string;
  Published?: boolean;
  TLC?: boolean;
  AllowNegotiatingPrice?: boolean;
  RentalType?: number;
  PricingPeriod?: number;
  KillSwitchId?: string;
  VerraTollTagAttached?: boolean;
  IsExternal?: boolean;
  RentersTag?: boolean;
  RequestShipTag?: boolean;
  TransponderNumber?: string;
}

export interface ImportVehiclesPayload {
  OwnerId?: string;
  CarTypeId: string;
  TollAccountId?: string;
  Vehicles: ImportVehicleRow[];
}

export interface ImportVehicleResult {
  rowIndex: number;
  success: boolean;
  carId?: string;
  wasUpdated: boolean;
  error?: string;
}

export interface VinDecodeResult {
  vin: string;
  make?: string;
  model?: string;
  year?: number;
  error?: string;
}

// Bulk price update from the Rates page tree. Pass `carIds` with the exact cars resolved for the
// clicked node (category / make / model / car). Only the supplied tiers are written; the backend
// always clamps the scope to the owner's own cars.
export interface BulkUpdateCarPricePayload {
  carIds?: string[];
  carId?: string;
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
}

export interface BulkLinkCandidate {
  id: string;
  title?: string;
  year?: number;
  plate?: string;
}

export interface BulkLinkResultItem {
  url: string;
  vehicleId?: string;
  make?: string;
  model?: string;
  status: 'linked' | 'ambiguous' | 'no-match' | 'already-linked' | 'invalid';
  carId?: string;
  candidates?: BulkLinkCandidate[];
}

// One external-platform listing linked to a car. A car may carry MANY of these — the same physical car
// listed on several Turo accounts, each with its own globally-unique vehicle id (many->one).
export interface ExternalVehicleLink {
  externalVehicleId: string;
  source?: string;      // "turo"; null for an unrecognized plugin id
  isPrimary: boolean;   // also held in the denormalized Cars.ExternalVehicleId scalar
  updatedAtUtc: string;
}

/** What the vehicle lists ask the server for: one page, already filtered. */
export interface CarPageQuery {
  /** 1-based. */
  page: number;
  pageSize: number;
  search?: string;
  /** The page's own vehicle type (Car Sharing / Commercial / plates). Omit on All Vehicles. */
  scopeTypeId?: string;
  typeIds?: string[];
  statusKeys?: string[];
  /** 'noAgency' | 'noTransponder' | 'ok' — see utils/tollCoverage. */
  tollLevels?: string[];
}

export interface CarPageResult {
  items: Car[];
  totalCount: number;
  page: number;
  pageSize: number;
  /** Type options for the toolbar, across the whole fleet — NOT just the rows on this page. */
  availableTypes: { id: string; name: string }[];
}

export const carsApi = {
  // Get all cars for owner
  getAllCars: async (): Promise<Car[]> => {
    const response = await apiClient.post<APIResponse<CarItemResponse[]>>(
      '/Cars/all',
      { role: RequestRole.Owner }
    );
    return response.data.result || [];
  },

  // Get all car details
  getAllCarDetails: async (): Promise<Car[]> => {
    const response = await apiClient.post<APIResponse<CarApiResponse[]>>(
      '/Cars/AllCarList',
      { role: RequestRole.Owner }
    );
    const apiCars = response.data.result || [];
    return apiCars.map(mapApiResponseToCar);
  },

  /**
   * One page of the owner's vehicles, filtered and counted by the server.
   *
   * Everything the toolbar offers travels with the request, because a page can only be right if the
   * filtering happened before the paging: filter on the client and page 1 of 40 rows would show the
   * handful that survived, while the pager still counted 4 pages.
   *
   * `userId` names the owner when an admin is viewing someone else's fleet; omit it for your own.
   */
  getCarPage: async (query: CarPageQuery, userId?: string): Promise<CarPageResult> => {
    const body = {
      role: RequestRole.Owner,
      page: query.page,
      pageSize: query.pageSize,
      search: query.search || '',
      scopeTypeId: query.scopeTypeId || null,
      typeIds: query.typeIds || [],
      statusKeys: query.statusKeys || [],
      tollLevels: query.tollLevels || [],
    };
    const url = userId ? `/Cars/AllCarList/page/${encodeURIComponent(userId)}` : '/Cars/AllCarList/page';
    const response = await apiClient.post<APIResponse<{
      items: CarApiResponse[];
      totalCount: number;
      page: number;
      pageSize: number;
      availableTypes: { id: string; name: string }[];
    }>>(url, body);
    const result = response.data.result;
    return {
      items: (result?.items || []).map(mapApiResponseToCar),
      totalCount: result?.totalCount ?? 0,
      page: result?.page ?? query.page,
      pageSize: result?.pageSize ?? query.pageSize,
      availableTypes: result?.availableTypes || [],
    };
  },

  // Get all car details by user ID (for admin)
  getAllCarDetailsByUserId: async (userId: string): Promise<Car[]> => {
    const response = await apiClient.get<APIResponse<CarApiResponse[]>>(
      `/Cars/AllCarList/${userId}`
    );
    const apiCars = response.data.result || [];
    return apiCars.map(mapApiResponseToCar);
  },

  // Car availability-status catalog (for the vehicles status multi-select filter).
  getCarStatuses: async (): Promise<CarStatusOption[]> => {
    try {
      const response = await apiClient.get<APIResponse<CarStatusOption[]>>('/Cars/statuses');
      return response.data.result || [];
    } catch {
      return [];
    }
  },

  // Get single car
  getCar: async (carId: string): Promise<Car | null> => {
    const response = await apiClient.get<APIResponse<Car>>(
      `/Cars/${carId}`
    );
    return response.data.result || null;
  },

  // Create/Update car
  saveCar: async (car: CarDetailsRequest): Promise<APIResponse<Car>> => {
    const response = await apiClient.post<APIResponse<Car>>(
      '/Cars',
      car
    );
    return response.data;
  },

  // Show/hide one car on the My E-Z Wheels storefront. Sends no car payload (like the publish
  // toggle), so a quick switch from the row context menu can't overwrite anything else.
  setShowInMyEzWheels: async (carId: string, show: boolean): Promise<void> => {
    await apiClient.put(`/Cars/${carId}/myezwheels/${show ? 1 : 0}`);
  },

  // Update car
  updateCar: async (car: CarDetailsRequest): Promise<APIResponse<Car>> => {
    const response = await apiClient.post<APIResponse<Car>>(
      '/Cars/update',
      car
    );
    return response.data;
  },

  // Link (or re-link) a platform vehicle id to this car so booking EMAILS resolve to it EXACTLY by id,
  // without the platform API. `idOrUrl` may be the raw id ("3792318") or the full listing URL
  // (turo.com/us/en/car-rental/.../nissan/altima/3792318); empty clears the link. Returns the updated car.
  linkExternalVehicle: async (
    carId: string,
    idOrUrl: string,
    source: string = 'turo'
  ): Promise<APIResponse<Car>> => {
    const response = await apiClient.post<APIResponse<Car>>(
      `/Cars/${carId}/link-external-vehicle`,
      { idOrUrl, source, role: RequestRole.Owner }
    );
    return response.data;
  },

  // Bulk-link: paste several Turo listing URLs at once. Backend parses vehicleId+make+model from each and
  // auto-links the ones that map to exactly one owner car of that make/model; ambiguous / no-match are returned
  // for the owner to resolve. Zero Turo calls. Re-runnable / incremental.
  linkExternalVehiclesBulk: async (
    urls: string[],
    source: string = 'turo'
  ): Promise<BulkLinkResultItem[]> => {
    const response = await apiClient.post<APIResponse<BulkLinkResultItem[]>>(
      '/Cars/link-external-vehicles-bulk',
      { urls, source, role: RequestRole.Owner }
    );
    return response.data.result || [];
  },

  // List every external listing id linked to this car. Many->one: the same physical car can be listed on
  // several Turo accounts, each with its own id — this returns the full set (linkExternalVehicle appends).
  listExternalVehicles: async (carId: string): Promise<ExternalVehicleLink[]> => {
    const response = await apiClient.get<APIResponse<ExternalVehicleLink[]>>(
      `/Cars/${carId}/external-vehicles`,
      { params: { role: RequestRole.Owner } }
    );
    return response.data.result || [];
  },

  // Remove ONE linked listing id (unlink a single Turo account's listing while keeping the others).
  unlinkExternalVehicle: async (
    carId: string,
    externalVehicleId: string
  ): Promise<boolean> => {
    const response = await apiClient.delete<APIResponse<boolean>>(
      `/Cars/${carId}/external-vehicles/${encodeURIComponent(externalVehicleId)}`,
      { params: { role: RequestRole.Owner } }
    );
    return !!response.data.result;
  },

  // Delete car (soft delete)
  deleteCar: async (carId: string): Promise<APIResponse<null>> => {
    const response = await apiClient.delete<APIResponse<null>>(
      `/Cars/${carId}`
    );
    return response.data;
  },

  // How an admin's delete works on this server: permanent = hard delete; false (production) = mark deleted only
  getDeleteMode: async (): Promise<{ permanent: boolean }> => {
    const response = await apiClient.get<APIResponse<{ permanent: boolean }>>('/Cars/delete-mode');
    return { permanent: !!response.data.result?.permanent };
  },

  // Hard delete car and ALL related records (admin only)
  hardDeleteCar: async (carId: string): Promise<APIResponse<null>> => {
    const response = await apiClient.delete<APIResponse<null>>(
      `/Cars/hard-delete/${carId}`
    );
    return response.data;
  },

  // Hand a car over to another owner with its toll history; its toll-agency tags are switched off (admin only)
  transferCarOwner: async (carId: string, newOwnerId: string): Promise<APIResponse<unknown>> => {
    const response = await apiClient.post<APIResponse<unknown>>(
      `/Cars/${carId}/transfer-owner`,
      { newOwnerId }
    );
    return response.data;
  },

  // Import vehicles (bulk upsert)
  importVehicles: async (payload: ImportVehiclesPayload): Promise<ImportVehicleResult[]> => {
    const response = await apiClient.post<APIResponse<ImportVehicleResult[]>>(
      '/Cars/import',
      payload
    );
    return response.data.result || [];
  },

  // Bulk-update rental price tiers across the owner's fleet (Rates page). Returns # of cars updated.
  bulkUpdatePrice: async (payload: BulkUpdateCarPricePayload): Promise<number> => {
    const response = await apiClient.post<APIResponse<number>>(
      '/Cars/bulk-update-price',
      { role: RequestRole.Owner, ...payload }
    );
    return response.data.result ?? 0;
  },

  // Decode VINs via NHTSA
  decodeVins: async (vins: string[]): Promise<VinDecodeResult[]> => {
    const response = await apiClient.post<APIResponse<VinDecodeResult[]>>(
      '/Cars/decode-vins',
      { vins }
    );
    return response.data.result || [];
  },

  // Get car types
  getCarTypes: async (): Promise<CarType[]> => {
    const response = await apiClient.get<APIResponse<CarType[]>>(
      '/Cars/types'
    );
    return response.data.result || [];
  },

  // Get car brands (makes) from the model catalog, filtered by sales region (country); default US.
  getCarBrands: async (region: string = 'US'): Promise<string[]> => {
    const response = await apiClient.get<APIResponse<string[]>>(
      `/Cars/brands?region=${encodeURIComponent(region)}`
    );
    return response.data.result || [];
  },

  // Models for a given make in a region (default US) — for the Model dropdown.
  getCarModels: async (make: string, region: string = 'US'): Promise<string[]> => {
    if (!make) return [];
    const response = await apiClient.get<string[]>(
      `/api/car-catalog/models?make=${encodeURIComponent(make)}&region=${encodeURIComponent(region)}`
    );
    return response.data || [];
  },

  // Catalog regions (countries) for the region selector.
  getCatalogRegions: async (): Promise<{ code: string; name: string }[]> => {
    const response = await apiClient.get<{ code: string; name: string }[]>(
      '/api/car-catalog/regions'
    );
    return response.data || [];
  },

  // Get insurances (Liability)
  getInsurances: async (): Promise<CarInsurance[]> => {
    const response = await apiClient.get<APIResponse<CarInsurance[]>>(
      '/Cars/insurance/0'
    );
    return response.data.result || [];
  },

  // Get full coverage insurances
  getFullCoverageInsurances: async (): Promise<CarInsurance[]> => {
    const response = await apiClient.get<APIResponse<CarInsurance[]>>(
      '/Cars/insurance/1'
    );
    return response.data.result || [];
  },

  // Search cars by license plate
  searchByLicensePlate: async (plates: string): Promise<Car[]> => {
    // Bare JSON string body — axios sets no content type for a string, and the browser's text/plain
    // fallback makes the API answer 415. See authApi.checkEmailAvailable.
    const response = await apiClient.post<APIResponse<CarApiResponse[]>>(
      '/Cars/CarListByLicensePlate',
      JSON.stringify(plates),
      { headers: { 'Content-Type': 'application/json' } }
    );
    const apiCars = response.data.result || [];
    return apiCars.map(mapApiResponseToCar);
  },

};
